<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Check, ChevronLeft, Coins, Flame } from 'lucide-vue-next'
import { apiGetPointRechargePackages } from '@/apis/membership'
import type { IPointRechargePackage, IResPointRechargePackages } from '@/types/apis/membership'
import { PaymentProvider } from '@/enums/membership'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { getLocale } from '@/i18n'
import PaymentPopup from '@/pages/plan/components/payment-popup.vue'
import type { IPurchaseSummary } from './types'

const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()

const pointsInfo = ref<IResPointRechargePackages>()
const packages = computed(() => pointsInfo.value?.Packages ?? [])
const selectedRechargePoints = ref<number | null>(null)
const loadingPackages = ref(false)
const paymentPopupVisible = ref(false)

const isChineseLocale = computed(() => getLocale() === 'zh-CN')
const sortedPackages = computed(() => [...packages.value].sort((a, b) => b.RechargePoints - a.RechargePoints))
const selectedPackage = computed(
  () => sortedPackages.value.find(item => item.RechargePoints === selectedRechargePoints.value) ?? sortedPackages.value[0] ?? null,
)

const selectedPackageSummary = computed<IPurchaseSummary | null>(() => {
  if (!selectedPackage.value) return null

  return {
    title: t('component.planPage.pointsPackageTitle', { points: selectedPackage.value.RechargePoints }),
    description:
      selectedPackage.value.ValidityDescription || t('component.planPage.pointsPackageFallbackDescription'),
    priceCny: selectedPackage.value.CnyAmount,
    priceUsd: selectedPackage.value.UsdAmount,
    validityDays: selectedPackage.value.ValidityDays,
    validityDescription: selectedPackage.value.ValidityDescription,
    points: selectedPackage.value.RechargePoints,
  }
})

const formatPackagePrice = (item: IPointRechargePackage) => {
  const price = isChineseLocale.value ? item.CnyAmount : item.UsdAmount
  const symbol = isChineseLocale.value ? '¥' : '$'
  return `${symbol}${price.toFixed(2)}`
}

const openPaymentPopup = (item: IPointRechargePackage) => {
  selectedRechargePoints.value = item.RechargePoints
  paymentPopupVisible.value = true
}

interface IPaymentResultPayload {
  orderNo: string
  paymentProvider: PaymentProvider
  channelOrderId: string
  planId: number
}

const handlePaid = (payload: IPaymentResultPayload) => {
  router.replace({
    path: '/plan/result',
    query: {
      orderNo: payload.orderNo,
      paymentProvider: String(payload.paymentProvider),
      channelOrderId: payload.channelOrderId,
      planId: String(payload.planId),
    },
  })
}

const initPackages = async () => {
  loadingPackages.value = true
  try {
    const res = await apiGetPointRechargePackages()
    pointsInfo.value = res.Body
  } finally {
    loadingPackages.value = false
  }
}

watch(
  sortedPackages,
  nextPackages => {
    if (!selectedRechargePoints.value && nextPackages.length > 0) {
      selectedRechargePoints.value = nextPackages[0]?.RechargePoints ?? null
    }
  },
  { immediate: true },
)

onMounted(async () => {
  await Promise.all([initPackages(), userStore.refreshProfileInfo()])
})
</script>

<template>
  <div class="h-screen overflow-y-auto bg-gray-950 text-white">
    <div class="mx-auto max-w-6xl px-4 pb-28 pt-5 sm:px-6 lg:px-8 lg:pb-10">
      <div class="grid gap-6">
        <button class="inline-flex items-center gap-2 text-sm text-white/80" @click="router.back()">
          <ChevronLeft :size="24" />
          {{ t('plan.page.back') }}
        </button>

        <section
          class="overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(245,158,11,0.24),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-4 py-8 shadow-[0_24px_80px_rgba(15,23,42,0.4)] sm:p-8"
        >
          <div class="mb-6 flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div class="text-sm text-white/50">{{ t('component.planPage.pointsBalance') }}</div>
              <div class="mt-2 text-2xl font-semibold text-white flex items-center gap-2">
                <Flame :size="22"  fill="currentcolor"/>
                {{ userStore.profileInfo?.AvailablePoints ?? 0 }}
              </div>
              <p class="mt-2 text-sm leading-6 text-white/60">{{ t('component.planPage.pointsEntryDescription') }}</p>

            </div>
            <button
              class="inline-flex h-11 items-center justify-center rounded-2xl border border-white/15 bg-white/8 px-5 text-sm text-white/80 transition hover:bg-white/12 hover:text-white"
              @click="router.push('/plan')"
            >
              {{ t('component.planPage.viewMembershipPlans') }}
            </button>
          </div>



          <div class="flex flex-col gap-6" v-if="appStore.isMobile">
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="item in sortedPackages"
                :key="item.RechargePoints"
                class="rounded-2xl border px-4 py-2 text-sm font-medium transition-all duration-200"
                :class="
                  selectedRechargePoints === item.RechargePoints
                    ? 'border-amber-300/60 bg-amber-300/15 text-white'
                    : 'border-white/10 bg-white/5 text-white/60'
                "
                @click="selectedRechargePoints = item.RechargePoints"
              >
                <!-- {{ t('component.planPage.pointsChip', { points: item.RechargePoints }) }} -->
                {{ item.RechargePoints }}
              </button>
            </div>

            <div
              class="overflow-hidden rounded-3xl border border-amber-300/70 bg-amber-300/10  px-4 py-6"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <div class="text-xl font-semibold">
                    {{
                      selectedPackage
                        ? t('component.planPage.pointsPackageTitle', { points: selectedPackage.RechargePoints })
                        : t('component.planPage.pleaseSelectPointsPackage')
                    }}
                  </div>
                  <div class="mt-2 text-sm text-white/60">
                    {{ t('component.planPage.validityDays', { days: selectedPackage?.ValidityDays ?? 0 }) }}
                  </div>
                </div>
                <div class="rounded-full bg-white/10 px-3 py-1 text-xs text-white/75">
                  {{ t('component.planPage.rechargePoints', { points: selectedPackage?.RechargePoints ?? 0 }) }}
                </div>
              </div>

              <div class="mt-6 flex items-end gap-2">
                <span class="text-4xl font-semibold text-white">
                  {{ selectedPackage ? formatPackagePrice(selectedPackage) : '--' }}
                </span>
              </div>

              <p class="mt-4 min-h-12 text-sm leading-6 text-white/70">
                {{
                  selectedPackage?.ValidityDescription ||
                  selectedPackageSummary?.description ||
                  t('component.planPage.pointsPackageFallbackDescription')
                }}
              </p>

              <div class="mt-5 flex flex-col gap-2">
                <div class="flex items-center gap-2 text-sm text-white/75">
                  <Check :size="16" />
                  {{ t('component.planPage.rechargePoints', { points: selectedPackage?.RechargePoints ?? 0 }) }}
                </div>
                <div class="flex items-center gap-2 text-sm text-white/75">
                  <Check :size="16" />
                  {{ t('component.planPage.validityDays', { days: selectedPackage?.ValidityDays ?? 0 }) }}
                </div>
              </div>
            </div>
          </div>

          <template v-else>
            <div class="max-w-2xl mb-4 sm:mb-8">
              <h1 class="text-lg sm:text-2xl font-semibold tracking-wide">{{ t('plan.page.pointsTitle') }}</h1>
              <p class="mt-3 max-w-xl text-sm leading-6 text-white/65 sm:text-base">
                {{ pointsInfo?.ValidityDescription }}
              </p>
            </div>
            <div class="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3 py-8">
              <div
                v-for="item in sortedPackages"
                :key="item.RechargePoints"
                class="group cursor-pointer relative overflow-hidden rounded-3xl border p-5 text-left transition-all duration-200"
                :class="
                  selectedRechargePoints === item.RechargePoints
                    ? 'border-amber-300/60 bg-amber-300/10 shadow-[0_12px_40px_rgba(245,158,11,0.14)]'
                    : 'border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5'
                "
                @click="selectedRechargePoints = item.RechargePoints"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-lg font-semibold flex items-center gap-2">
                      {{ t('component.planPage.pointsPackageTitle', { points: item.RechargePoints }) }}
                    </div>
                    <div class="mt-1 text-xs text-white/50 text-nowrap">
                      {{ t('component.planPage.validityDays', { days: item.ValidityDays }) }}
                    </div>
                  </div>
                  <div class="rounded-full bg-white/8 px-3 py-1 text-xs text-white/70">
                    {{ t('component.planPage.rechargePoints', { points: item.RechargePoints }) }}
                  </div>
                </div>

                <div class="mt-6 flex items-end gap-2">
                  <span class="text-3xl font-semibold text-white">{{ formatPackagePrice(item) }}</span>
                </div>

                <p class="mt-4 min-h-16 text-sm leading-6 text-white/60">
                  {{ item.ValidityDescription || t('component.planPage.pointsPackageFallbackDescription') }}
                </p>

                <div class="mt-4 flex flex-col gap-2">
                  <div class="flex items-center gap-2 text-sm text-white/75">
                    <Check :size="16" />
                    {{ t('component.planPage.rechargePoints', { points: item.RechargePoints }) }}
                  </div>
                  <div class="flex items-center gap-2 text-sm text-white/75">
                    <Check :size="16" />
                    {{ t('component.planPage.validityDays', { days: item.ValidityDays }) }}
                  </div>
                </div>

                <button
                  :class="[selectedRechargePoints === item.RechargePoints ? 'visible' : 'invisible']"
                  class="mt-12 flex h-11 w-full items-center justify-center rounded-2xl bg-amber-400 text-sm font-semibold text-slate-900 transition disabled:cursor-not-allowed! disabled:opacity-60"
                  :disabled="loadingPackages"
                  @click.stop="openPaymentPopup(item)"
                >
                  {{ t('component.planPage.buyNow') }}
                </button>
              </div>
            </div>
          </template>


          <div v-if="!loadingPackages && sortedPackages.length === 0" class="py-16 text-center text-white/45">
            {{ t('component.planPage.noPointsPackages') }}
          </div>
        </section>
      </div>
    </div>

    <div
      v-if="appStore.isMobile"
      class="fixed inset-x-0 bottom-0 z-20 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-4 backdrop-blur"
    >
      <button
        class="flex h-12 w-full items-center justify-center rounded-2xl bg-amber-400 text-base font-semibold text-slate-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-20"
        :disabled="loadingPackages || !selectedPackage"
        @click="selectedPackage && openPaymentPopup(selectedPackage)"
      >
        {{ t('component.planPage.buyNow') }}
      </button>
    </div>

    <PaymentPopup
      v-model:visible="paymentPopupVisible"
      business-type="points"
      :plan-id="0"
      :recharge-points="selectedPackage?.RechargePoints ?? 0"
      :purchase-summary="selectedPackageSummary"
      @paid="handlePaid"
    />
  </div>
</template>